import React from 'react';
import PropTypes from 'prop-types';
import { initAuthorize } from './lib/scripts/page-specific/oauth2/authorize';
import config from './lib/config';
import { useOktaAuth } from '@okta/okta-react';
import { getXRoutingDeploymentValue, setAndGetLwaUniqId } from './lib/cookie-utils';
import { createHashFromString } from './lib/amplitude-utils';

const customLinksAthena =
  [
    {
      href: 'https://www.athenahealth.com/resources/patient-facing-applications-terms-of-use',
      text: 'Terms of Use',
      target: '_blank',
    },
    {
      href: 'https://www.athenahealth.com/resources/privacy-notice-for-patient-facing-applications',
      text: 'Privacy Policy',
      target: '_blank',
    },
  ];

const defaultProps = {
  iamCoreUrl: config.iamCoreUrl,
  oktaUrl: config.oktaURL,
  supportEnv: '',
  forgotPasswordAthena: 'signin/athena-forgot-password',
  customLinksAthena: JSON.stringify(customLinksAthena),
  redirectUri: config.redirectUri,
  renderWidgetFunction: initAuthorize,
  features: JSON.stringify({
    idpDiscovery: config.oktaIdpDiscovery,
    showPasswordToggleOnSignInPage: config.showPasswordToggleOnSignInPage,
  }),
  consentOktaUrl: config.oktaURL,
  amplitudeApiKey: config.amplitudeApiKey,
  scope: config.scopes.join(' '),
  rolloutAuthenticatorAppFactor: config.rolloutAuthenticatorAppFactor,
  xRoutingDeployment: getXRoutingDeploymentValue(),
};
export default function OktaSignInWidget(props) {
  props = { ...defaultProps, ...props };
  const { oktaAuth } = useOktaAuth();
  const lwaUniqId = setAndGetLwaUniqId();
  const amplitudeUserId = props.username ? createHashFromString(props.username) : '';
  React.useEffect(() => {
    props.renderWidgetFunction.call(undefined, oktaAuth);
    return () => {
      if (window.oktaSignIn) {
        window.oktaSignIn.off();
        window.oktaSignIn.remove();
      }
    };
  }, []);

  return (
    <div id="okta-login-container" data-okta-url={props.oktaUrl}
      data-consent-okta-url={props.consentOktaUrl}
      data-athenanet={props.athenanet}
      data-amplitude-user-id={amplitudeUserId}
      data-amplitude-device-id={props.amplitudeDeviceId}
      data-authorize-redirect-url={props.authorizeRedirectUrl}
      data-forgot-password-athena={props.forgotPasswordAthena}
      data-proxied-authorize-values={props.proxiedAuthorizeValues}
      data-support-env={props.supportEnv}
      data-create-account={props.createAccount}
      data-prompt={props.prompt}
      data-username={props.username}
      data-session-token={props.sessionToken}
      // When redirecting to Okta for pre-auth, the state token shouldn't be set in AOSW widget as it is expected to be in classic mode, but the token is in V2 format and thus fails to render the widget
      data-state-token={undefined}
      data-application-id={props.clientId}
      data-access-token={props.accessToken}
      data-id-token={props.idToken}
      data-redirect-uri={props.redirectUri}
      data-language={props.language}
      data-logo-url={props.logoUrl}
      data-logo-text={props.logoText}
      data-post-authn-routes={props.postAuthnRoutes}
      data-iam-core-url={props.iamCoreUrl}
      data-common-login-identity-url={props.commonLoginIdentityUrl}
      data-custom-links-athena={props.customLinksAthena}
      data-custom-links={props.customLinksAthena}
      data-i18n={props.i18n}
      data-display-name={props.displayName}
      data-scope={props.scope}
      data-user-state={props.userState}
      data-sanitize-info={props.sanitizeInfo}
      data-amplitude-api-key={props.amplitudeApiKey}
      data-test-app={props.testApp}
      data-workflow={props.workflow}
      data-idp={props.idp}
      data-features={props.features}
      data-workflow-type={props.workflowType}
      data-new-user={props.newUser}
      data-server-time={props.serverTime}
      data-lwa-uniq-id={lwaUniqId}
      data-device-id={props.deviceId}
      data-relogin={props.relogin}
      data-workflow-handler-endpoint={props.workflowHandlerEndpoint}
      data-aud={props.aud}
      data-state={props.state}
      data-email-token={props.emailToken}
      data-hide-reset-account-option={props.hideResetAccountOption}
      data-app-clientid={props.clientId}
      data-show-terms-text-on-login={props.showTermsTextOnLogin}
      data-show-terms-text={props.showTermsText}
      data-iframe-url={props.loginFrameUrl}
      data-user-app-type-mismatch-error={props.userAppTypeMismatchError}
      data-email-token-logged-in-user-mismatch-error={props.emailTokenLoggedInUserMismatchError}
      data-user-namespace={props.userNamespace}
      data-manage-profile-error={props.manageProfileError}
      data-okta-org-id={props.oktaOrgId}
      data-email-token-user-registered-warning={props.emailTokenUserRegisteredWarning}
      data-temp-password-hint={props.tempPasswordHint}
      data-show-set-password-description={props.showSetPasswordDescription}
      data-skip-patient-summary={props.skipPatientSummary}
      data-app-type={props.appType}
      data-hide-remember-me={props.hideRememberMe}
      data-user-mfa-policy-tier={props.userMfaPolicyTier}
      data-context-mfa-policy-tier={props.contextMfaPolicyTier}
      data-rollout-authenticator-app-factor={props.rolloutAuthenticatorAppFactor}
      data-call-process-creds={props.callProcessCreds}
      data-update-user-mfa-profile={props.updateUserMfaProfile}
      data-authorize-post-params={props.authorizePostParams}
      data-non-degradable-error={props.nonDegradableError}
      data-signup-link={props.signupLink}
      data-x-routing-deployment={props.xRoutingDeployment}
      data-okta-user-id={props.oktaUserId}
    >
    </div>
  );
}

OktaSignInWidget.propTypes = {
  oktaUrl: PropTypes.string,
  consentOktaUrl: PropTypes.string,
  athenanet: PropTypes.string,
  userId: PropTypes.string,
  amplitudeUserId: PropTypes.string,
  amplitudeDeviceId: PropTypes.string,
  authorizeRedirectUrl: PropTypes.string,
  forgotPasswordAthena: PropTypes.string,
  proxiedAuthorizeValues: PropTypes.string,
  supportEnv: PropTypes.string,
  createAccount: PropTypes.bool,
  prompt: PropTypes.string,
  username: PropTypes.string,
  sessionToken: PropTypes.string,
  stateToken: PropTypes.string,
  accessToken: PropTypes.string,
  idToken: PropTypes.string,
  redirectUri: PropTypes.string,
  language: PropTypes.string,
  logoUrl: PropTypes.string,
  logoText: PropTypes.string,
  postAuthnRoutes: PropTypes.string,
  iamCoreUrl: PropTypes.string,
  commonLoginIdentityUrl: PropTypes.string,
  customLinksAthena: PropTypes.string,
  i18n: PropTypes.string,
  displayName: PropTypes.string,
  scope: PropTypes.string,
  userState: PropTypes.string,
  sanitizeInfo: PropTypes.string,
  amplitudeApiKey: PropTypes.string,
  testApp: PropTypes.string,
  workflow: PropTypes.string,
  idp: PropTypes.string,
  features: PropTypes.string,
  workflowType: PropTypes.string,
  newUser: PropTypes.bool,
  serverTime: PropTypes.number,
  deviceId: PropTypes.string,
  relogin: PropTypes.bool,
  workflowHandlerEndpoint: PropTypes.string,
  aud: PropTypes.string,
  state: PropTypes.string,
  emailToken: PropTypes.string,
  hideResetAccountOption: PropTypes.bool,
  clientId: PropTypes.string,
  loginFrameUrl: PropTypes.string,
  renderWidgetFunction: PropTypes.func,
  showTermsTextOnLogin: PropTypes.bool,
  showTermsText: PropTypes.bool,
  userAppTypeMismatchError: PropTypes.string,
  emailTokenLoggedInUserMismatchError: PropTypes.string,
  userNamespace: PropTypes.string,
  manageProfileError: PropTypes.bool,
  oktaOrgId: PropTypes.string,
  showSetPasswordDescription: PropTypes.bool,
  emailTokenUserRegisteredWarning: PropTypes.bool,
  tempPasswordHint: PropTypes.bool,
  skipPatientSummary: PropTypes.bool,
  appType: PropTypes.string,
  hideRememberMe: PropTypes.bool,
  userMfaPolicyTier: PropTypes.string,
  contextMfaPolicyTier: PropTypes.string,
  rolloutAuthenticatorAppFactor: PropTypes.bool,
  callProcessCreds: PropTypes.bool,
  updateUserMfaProfile: PropTypes.bool,
  authorizePostParams: PropTypes.string,
  nonDegradableError: PropTypes.bool,
  signupLink: PropTypes.string,
  xRoutingDeployment: PropTypes.string,
  oktaUserId: PropTypes.string,
};
